'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@common/components';
import SvgMagnifyingGlass from '@common/icon/SvgMagnifyingGlass';
import SvgNotification from '@common/icon/SvgNotification';
import SvgShoppingBasketFill from '@common/icon/SvgShoppingBasketFill';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { Suspense } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { HeaderLogo } from '@/app/_components/HeaderLogo';
import { HeaderSearchContainer, useSearchOverlayStore } from '@/app/_components/SearchBar';
import { shouldShowDesktopTrendKeyword } from '@/app/_components/TrendKeyword/constants';
import { TrendKeywordInline } from '@/app/_components/TrendKeyword/TrendKeywordInline';
import { HeaderProfileAvatar } from '@/app/(allowed)/my/_components/HeaderProfileAvatar';
import { useMarketCart } from '@/app/welmarket/cart/_hooks/useMarketCart';
import { HIDE_DESKTOP_HEADER_PATHNAMES } from '@/components/template/GlobalLayout/constants';
import { DONATION_ROUTES, ROUTES, WEL_MARKET_ROUTES } from '@/constants';
import { SUB_DOMAIN } from '@/constants/domain';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { usePartnerChromeClassName } from '@/hooks/usePartnerChromeClassName';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { scrollToPageTop } from '@/utils';
interface DesktopNavItem {
  label: string;
  href: string;
  isActive: (pathname: string) => boolean;
}
const isSectionPath = (pathname: string, sectionRoot: string) => pathname === sectionRoot || pathname.startsWith(`${sectionRoot}/`);
const DESKTOP_NAV_ITEMS: DesktopNavItem[] = [{
  label: '홈',
  href: ROUTES.HOME.pathname,
  isActive: pathname => pathname === ROUTES.HOME.pathname
}, {
  label: '고향사랑기부',
  href: DONATION_ROUTES.HOME.pathname,
  isActive: pathname => isSectionPath(pathname, DONATION_ROUTES.HOME.pathname)
}, {
  label: '웰마켙',
  href: WEL_MARKET_ROUTES.HOME.pathname,
  isActive: pathname => isSectionPath(pathname, WEL_MARKET_ROUTES.HOME.pathname)
}];
export const DesktopHeader = () => {
  const $ = _c(62);
  const partnerChromeClassName = usePartnerChromeClassName();
  const isLogin = useAuthStore(_temp);
  const pathname = usePathname() ?? "";
  const subDomain = useDeviceInfoStore(_temp2);
  const router = useCustomRouter();
  const isOverlayOpen = useSearchOverlayStore(_temp3);
  const openOverlay = useSearchOverlayStore(_temp4);
  const closeOverlay = useSearchOverlayStore(_temp5);
  const isHiddenPage = HIDE_DESKTOP_HEADER_PATHNAMES.includes(pathname);
  let t0;
  if ($[0] !== pathname) {
    t0 = isSectionPath(pathname, WEL_MARKET_ROUTES.HOME.pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isLocalMarketPath = t0;
  const shouldHideTossLocalMarketHeader = isLocalMarketPath && subDomain === SUB_DOMAIN.TOSS;
  if (isHiddenPage || shouldHideTossLocalMarketHeader) {
    return null;
  }
  const isSearchPage = pathname === ROUTES.SEARCH.pathname;
  let t1;
  if ($[2] !== closeOverlay || $[3] !== isOverlayOpen || $[4] !== isSearchPage || $[5] !== openOverlay || $[6] !== router) {
    t1 = () => {
      if (!isSearchPage) {
        router.push(ROUTES.SEARCH.pathname);
        return;
      }
      (isOverlayOpen ? closeOverlay : openOverlay)();
    };
    $[2] = closeOverlay;
    $[3] = isOverlayOpen;
    $[4] = isSearchPage;
    $[5] = openOverlay;
    $[6] = router;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const handleSearchClick = t1;
  const isHomePage = pathname === ROUTES.HOME.pathname;
  let t2;
  if ($[8] !== isHomePage) {
    t2 = e => {
      if (!isHomePage) {
        return;
      }
      e.preventDefault();
      scrollToPageTop();
    };
    $[8] = isHomePage;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const handleHomeClick = t2;
  const isPartnerShell = subDomain === SUB_DOMAIN.TOSS || subDomain === SUB_DOMAIN.KAKAOBANK;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[10] !== handleHomeClick || $[11] !== isPartnerShell || $[12] !== partnerChromeClassName || $[13] !== pathname) {
    let t9;
    if ($[20] !== isPartnerShell) {
      t9 = tab => !(isPartnerShell && tab.href === WEL_MARKET_ROUTES.HOME.pathname);
      $[20] = isPartnerShell;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    const desktopNavItems = DESKTOP_NAV_ITEMS.filter(t9);
    if ($[22] !== partnerChromeClassName) {
      t8 = cn(partnerChromeClassName, "sticky top-0 z-20 w-full border-b border-Gray-200 bg-Gray-white mobile:hidden tablet:hidden");
      $[22] = partnerChromeClassName;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    t7 = "mx-auto flex h-72 max-w-screen-xl items-center justify-between px-40";
    t5 = "flex items-center gap-32";
    let t10;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <HeaderLogo />;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    if ($[25] !== handleHomeClick) {
      t6 = <CustomLink href={ROUTES.HOME.pathname} className="flex shrink-0 items-center" aria-label={"Wello \uD648"} onClick={handleHomeClick}>{t10}</CustomLink>;
      $[25] = handleHomeClick;
      $[26] = t6;
    } else {
      t6 = $[26];
    }
    t3 = "flex items-center gap-4";
    let t11;
    if ($[27] !== handleHomeClick || $[28] !== pathname) {
      t11 = tab_0 => <NavLink key={tab_0.href} href={tab_0.href} isActive={tab_0.isActive(pathname)} onClick={tab_0.href === ROUTES.HOME.pathname ? handleHomeClick : undefined}>{tab_0.label}</NavLink>;
      $[27] = handleHomeClick;
      $[28] = pathname;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    t4 = desktopNavItems.map(t11);
    $[10] = handleHomeClick;
    $[11] = isPartnerShell;
    $[12] = partnerChromeClassName;
    $[13] = pathname;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
  }
  let t9;
  if ($[30] !== t3 || $[31] !== t4) {
    t9 = <div className={t3}>{t4}</div>;
    $[30] = t3;
    $[31] = t4;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== t5 || $[34] !== t6 || $[35] !== t9) {
    t10 = <div className={t5}>{t6}{t9}</div>;
    $[33] = t5;
    $[34] = t6;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== pathname) {
    t11 = shouldShowDesktopTrendKeyword(pathname) && <TrendKeywordInline />;
    $[37] = pathname;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SvgMagnifyingGlass className="size-24" />;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== handleSearchClick) {
    t13 = <button type="button" aria-label={"\uAC80\uC0C9"} onClick={handleSearchClick} className="flex size-40 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900">{t12}</button>;
    $[40] = handleSearchClick;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <CustomLink href={ROUTES.PUSH.pathname} aria-label={"\uC54C\uB9BC\uC73C\uB85C \uC774\uB3D9"} className="flex size-40 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900"><SvgNotification className="size-24" /></CustomLink>;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== isLocalMarketPath) {
    t15 = isLocalMarketPath && <DesktopCartButton />;
    $[43] = isLocalMarketPath;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  let t17;
  if ($[45] !== isLogin) {
    t16 = <HeaderProfileAvatar showProfileAvatar={isLogin} />;
    t17 = isLogin === false && <Button asChild={true} data-no-donation-accent={true} dimensions="H40" roundness="capsule"><CustomLink href={ROUTES.LOGIN.pathname}>로그인</CustomLink></Button>;
    $[45] = isLogin;
    $[46] = t16;
    $[47] = t17;
  } else {
    t16 = $[46];
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t11 || $[49] !== t13 || $[50] !== t15 || $[51] !== t16 || $[52] !== t17) {
    t18 = <div className="flex items-center gap-12">{t11}{t13}{t14}{t15}{t16}{t17}</div>;
    $[48] = t11;
    $[49] = t13;
    $[50] = t15;
    $[51] = t16;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== t10 || $[55] !== t18 || $[56] !== t7) {
    t19 = <nav className={t7}>{t10}{t18}</nav>;
    $[54] = t10;
    $[55] = t18;
    $[56] = t7;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Suspense fallback={null}><HeaderSearchContainer /></Suspense>;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t19 || $[60] !== t8) {
    t21 = <header className={t8}>{t19}{t20}</header>;
    $[59] = t19;
    $[60] = t8;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  return t21;
};
const DesktopCartButton = () => {
  const $ = _c(5);
  const {
    totalCount
  } = useMarketCart();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SvgShoppingBasketFill className="size-24" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== totalCount) {
    t1 = totalCount > 0 && <span className="absolute right-4 top-4 flex size-16 items-center justify-center rounded-pill bg-R-500 font-700 text-Gray-white typo-caption2">{totalCount}</span>;
    $[1] = totalCount;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <CustomLink href={WEL_MARKET_ROUTES.CART.pathname} className="relative flex size-40 shrink-0 items-center justify-center p-0 text-Gray-700 transition-colors hover:text-Gray-900" aria-label={"\uC7A5\uBC14\uAD6C\uB2C8"}>{t0}{t1}</CustomLink>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const NavLink = t0 => {
  const $ = _c(8);
  const {
    href,
    isActive,
    children,
    onClick
  } = t0;
  const t1 = isActive ? "page" : undefined;
  const t2 = isActive ? "text-Gray-900" : "text-Gray-600 hover:text-Gray-900";
  let t3;
  if ($[0] !== t2) {
    t3 = cn("flex h-40 items-center px-12 font-600 transition-colors typo-body2", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== href || $[4] !== onClick || $[5] !== t1 || $[6] !== t3) {
    t4 = <CustomLink href={href} aria-current={t1} className={t3} onClick={onClick}>{children}</CustomLink>;
    $[2] = children;
    $[3] = href;
    $[4] = onClick;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
function _temp(state) {
  return state.isLogin;
}
function _temp2(state_0) {
  return state_0.subDomain;
}
function _temp3(state_1) {
  return state_1.isOverlayOpen;
}
function _temp4(state_2) {
  return state_2.openOverlay;
}
function _temp5(state_3) {
  return state_3.closeOverlay;
}